import type { SupportAccessRequest } from '@horevia/contracts';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Dialog,
  DialogClose,
  DialogContent,
  EmptyState,
  ErrorState,
  FormField,
  Input,
  NoPermissionState,
  PageHeader,
  Skeleton,
  Table,
  TableWrap,
  Td,
  Textarea,
  Th,
  Tr,
  toast,
} from '@horevia/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Ban, Check, LifeBuoy, ShieldOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '@/features/auth/session';
import { formatMinutes, supportStateTone } from '@/features/support/labels';
import {
  approveSupportAccess,
  declineSupportAccess,
  ownSupportAccessQueryOptions,
  revokeSupportAccess,
  supportKeys,
} from '@/features/support/queries';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatDateTime } from '@/lib/format';

export const Route = createFileRoute('/_app/admin/support')({ component: SupportAccessPage });

type Decision = { kind: 'approve' | 'decline' | 'revoke'; request: SupportAccessRequest };

/** Accès support, côté tenant (ADR-022) : le Owner approuve, refuse, révoque ; tout est journalisé. */
function SupportAccessPage() {
  const { t, i18n } = useTranslation(['support', 'common', 'errors']);
  const session = useSession();
  const canRead = session.can('support.read');
  const canDecide = session.can('support.decide');
  const query = useQuery(ownSupportAccessQueryOptions(canRead));
  const [decision, setDecision] = useState<Decision | null>(null);

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  return (
    <Container>
      <PageHeader
        title={t('support:title')}
        subtitle={t('support:subtitle')}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          { label: t('support:title') },
        ]}
      />
      <Card>
        <CardHeader
          title={t('support:listTitle')}
          icon={<LifeBuoy className="ico ico-16" aria-hidden="true" />}
        />
        <CardBody density="flush">
          {query.isPending ? (
            <div className="stack" style={{ padding: 16 }} aria-busy="true">
              <Skeleton height={48} />
              <Skeleton height={48} />
            </div>
          ) : query.isError || !query.data ? (
            <ErrorState
              title={t('common:states.errorTitle')}
              description={errorMessage(query.error, t)}
              traceId={errorTraceId(query.error)}
              retryLabel={t('common:actions.retry')}
              onRetry={() => void query.refetch()}
              traceCopyLabel={t('common:actions.copy')}
              traceCopiedLabel={t('common:actions.copied')}
            />
          ) : query.data.requests.length === 0 ? (
            <EmptyState title={t('support:none')} description={t('support:noneTenant')} />
          ) : (
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <Th>{t('support:columns.operator')}</Th>
                    <Th>{t('support:columns.reason')}</Th>
                    <Th>{t('support:columns.state')}</Th>
                    <Th>{t('support:columns.decision')}</Th>
                    <Th>{t('support:columns.actions')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {query.data.requests.map((request) => (
                    <Tr key={request.id} data-testid={`support-request-${request.id}`}>
                      <Td>
                        <div className="stack" style={{ gap: 2 }}>
                          <strong>{request.requestedBy.fullName}</strong>
                          <span className="t-12 muted">{request.requestedBy.email}</span>
                          <span className="t-12 muted">
                            {formatDateTime(request.createdAt, i18n.language)}
                          </span>
                        </div>
                      </Td>
                      <Td>
                        <div className="stack" style={{ gap: 2 }}>
                          <span>{request.reason}</span>
                          <span className="t-12 muted">
                            {t('support:requestedDuration', {
                              duration: formatMinutes(request.requestedMinutes),
                            })}
                          </span>
                        </div>
                      </Td>
                      <Td>
                        <span className="row row--wrap" style={{ gap: 4 }}>
                          <Badge tone={supportStateTone(request.state)}>
                            {t(`support:state.${request.state}`)}
                          </Badge>
                          {request.state === 'APPROVED' && request.expiresAt ? (
                            <span className="t-12 muted">
                              {t('support:until', {
                                date: formatDateTime(request.expiresAt, i18n.language),
                              })}
                            </span>
                          ) : null}
                        </span>
                      </Td>
                      <Td>
                        {request.decidedBy ? (
                          <div className="stack" style={{ gap: 2 }}>
                            <span>
                              {t('support:decidedBy', { name: request.decidedBy.fullName })}
                            </span>
                            {request.approvedMinutes ? (
                              <span className="t-12 muted">
                                {t('support:approvedDuration', {
                                  duration: formatMinutes(request.approvedMinutes),
                                })}
                              </span>
                            ) : null}
                            {request.decisionNote ? (
                              <span className="t-12 muted">{request.decisionNote}</span>
                            ) : null}
                            {request.requestsServed > 0 ? (
                              <span className="t-12 muted">
                                {t('support:requestsServed', { count: request.requestsServed })}
                              </span>
                            ) : null}
                          </div>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </Td>
                      <Td>
                        {canDecide && request.state === 'PENDING' ? (
                          <span className="row" style={{ gap: 4 }}>
                            <Button
                              size="sm"
                              variant="primary"
                              onClick={() => setDecision({ kind: 'approve', request })}
                            >
                              <Check className="ico ico-16" aria-hidden="true" />
                              {t('support:approve.action')}
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => setDecision({ kind: 'decline', request })}
                            >
                              <Ban className="ico ico-16" aria-hidden="true" />
                              {t('support:decline.action')}
                            </Button>
                          </span>
                        ) : canDecide && request.state === 'APPROVED' ? (
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => setDecision({ kind: 'revoke', request })}
                          >
                            <ShieldOff className="ico ico-16" aria-hidden="true" />
                            {t('support:revoke.action')}
                          </Button>
                        ) : null}
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          )}
        </CardBody>
      </Card>

      <DecisionDialog
        decision={decision}
        maxMinutes={query.data?.maxMinutes ?? 240}
        onClose={() => setDecision(null)}
      />
    </Container>
  );
}

function DecisionDialog({
  decision,
  maxMinutes,
  onClose,
}: {
  decision: Decision | null;
  maxMinutes: number;
  onClose: () => void;
}) {
  const { t } = useTranslation(['support', 'common', 'errors']);
  const queryClient = useQueryClient();
  const [minutes, setMinutes] = useState(60);
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!decision) return;
    setMinutes(Math.min(decision.request.requestedMinutes, maxMinutes));
    setNote('');
  }, [decision, maxMinutes]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!decision) return null;
      const trimmed = note.trim();
      const payload = trimmed ? { note: trimmed } : {};
      switch (decision.kind) {
        case 'approve':
          return approveSupportAccess(decision.request.id, { ...payload, minutes });
        case 'decline':
          return declineSupportAccess(decision.request.id, payload);
        case 'revoke':
          return revokeSupportAccess(decision.request.id, payload);
      }
    },
    onSuccess: async () => {
      if (decision) toast.success(t(`support:toasts.${decision.kind}d`));
      await queryClient.invalidateQueries({ queryKey: supportKeys.all });
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  if (!decision) return null;
  const { kind, request } = decision;
  const cap = Math.min(request.requestedMinutes, maxMinutes);
  const valid = kind !== 'approve' || (minutes >= 5 && minutes <= cap);

  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent
        title={t(`support:${kind}.title`)}
        description={t(`support:${kind}.description`, { name: request.requestedBy.fullName })}
        icon={
          kind === 'approve' ? (
            <Check className="ico" aria-hidden="true" />
          ) : (
            <Ban className="ico" aria-hidden="true" />
          )
        }
        iconTone={kind === 'approve' ? 'green' : 'red'}
        closeLabel={t('common:actions.close')}
        footer={
          <>
            <DialogClose asChild>
              <Button variant="secondary" type="button">
                {t('common:actions.cancel')}
              </Button>
            </DialogClose>
            <Button
              variant={kind === 'approve' ? 'primary' : 'danger'}
              type="button"
              disabled={!valid || mutation.isPending}
              onClick={() => mutation.mutate()}
            >
              {t(`support:${kind}.confirm`)}
            </Button>
          </>
        }
      >
        <div className="stack">
          <p className="t-14">
            <strong>{t('support:columns.reason')}</strong> — {request.reason}
          </p>
          {kind === 'approve' ? (
            <FormField label={t('support:approve.minutes', { max: cap })} required>
              {(field) => (
                <Input
                  id={field.id}
                  type="number"
                  min={5}
                  max={cap}
                  step={5}
                  value={minutes}
                  onChange={(event) => setMinutes(Number(event.target.value))}
                />
              )}
            </FormField>
          ) : null}
          <FormField label={t('support:note')}>
            {(field) => (
              <Textarea
                id={field.id}
                rows={3}
                maxLength={500}
                value={note}
                onChange={(event) => setNote(event.target.value)}
              />
            )}
          </FormField>
        </div>
      </DialogContent>
    </Dialog>
  );
}
